import { useState, useMemo, useCallback } from "react";
import { t } from "@lingui/macro";
import {
  CircleNotch,
  Brain,
  Sparkle,
  TrendUp,
  Rocket,
  Target,
  Info,
  CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { useTheme } from "@reactive-resume/hooks";
import { Gauge } from "@/client/components/gauge";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";

interface OverallScoreWithOptimizeProps {
  analysesData: AnalyzeResumeResult;
  onOptimize: () => void;
  isOptimizing: boolean;
  isGeneratingQuestions: boolean;
  isLoadingOptimization: boolean;
  optimizationError: string | Error | null;
}

// Constants moved outside component to prevent recreating on each render
const SCORE_THRESHOLDS = {
  EXCELLENT: 85,
  GOOD: 70,
} as const;

const URGENCY_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

// Score level configuration
const getScoreLevelConfig = (score: number, isDarkMode: boolean) => {
  if (score >= SCORE_THRESHOLDS.EXCELLENT) {
    return {
      level: t`Excellent Match`,
      description: t`Your resume strongly aligns with job requirements. AI optimization can make it absolutely perfect!`,
      color: isDarkMode ? "#66bb6a" : "#81c784",
      bgColor: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
      icon: Target,
      urgency: URGENCY_LEVELS.LOW,
      potential: t`+15-25% improvement possible`,
      buttonText: t`Perfect Resume`,
      tooltipText: t`Achieve resume perfection`,
      motivationalText: t`🎯 Perfect your already strong resume!`,
    };
  } else if (score >= SCORE_THRESHOLDS.GOOD) {
    return {
      level: t`Good Foundation`,
      description: t`Solid base with significant room for improvement. AI can transform this into an exceptional resume!`,
      color: "#ffb74d",
      bgColor: isDarkMode ? "rgba(255, 183, 77, 0.15)" : "rgba(255, 183, 77, 0.1)",
      icon: TrendUp,
      urgency: URGENCY_LEVELS.MEDIUM,
      potential: t`+30-50% improvement expected`,
      buttonText: t`Boost Resume`,
      tooltipText: t`Stand out from other candidates`,
      motivationalText:
        score >= 60
          ? t`✨ Unlock your resume's hidden potential!`
          : t`🔥 Transform your resume completely!`,
    };
  } else {
    return {
      level: t`High Potential`,
      description: t`Major transformation opportunity! AI optimization can dramatically boost your resume's impact.`,
      color: isDarkMode ? "#ef5350" : "#e57373",
      bgColor: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
      icon: Rocket,
      urgency: URGENCY_LEVELS.HIGH,
      potential: t`+50-80% improvement likely`,
      buttonText: t`Resolve Issues`,
      tooltipText: t`Dramatically increase interview chances`,
      motivationalText: t`🔥 Transform your resume completely!`,
    };
  }
};

// Button styling configuration
const getButtonStyling = (urgency: string) => {
  const styles = {
    [URGENCY_LEVELS.HIGH]: {
      gradient: "from-purple-600 via-blue-600 to-indigo-600",
      hoverGradient: "hover:from-purple-500 hover:via-blue-500 hover:to-indigo-500",
      glowColor: "from-purple-500 via-blue-500 to-indigo-500",
    },
    [URGENCY_LEVELS.MEDIUM]: {
      gradient: "from-indigo-600 via-purple-600 to-pink-600",
      hoverGradient: "hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500",
      glowColor: "from-indigo-500 via-purple-500 to-pink-500",
    },
    [URGENCY_LEVELS.LOW]: {
      gradient: "from-blue-600 via-indigo-600 to-purple-600",
      hoverGradient: "hover:from-blue-500 hover:via-indigo-500 hover:to-purple-500",
      glowColor: "from-blue-500 via-indigo-500 to-purple-500",
    },
  };

  return styles[urgency as keyof typeof styles] || styles[URGENCY_LEVELS.LOW];
};

// Error message component for better separation
const ErrorMessage = ({ error }: { error: string | Error | null }) => {
  if (!error) return null;

  const errorMessage =
    typeof error === "string"
      ? error
      : t`Optimization failed. Please try again - your perfect resume is just one click away!`;

  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-center text-sm text-red-600 dark:text-red-400">
        <Info className="size-4 flex-shrink-0" />
        <span>{errorMessage}</span>
      </div>
    </div>
  );
};

// Loading state component
const LoadingSpinner = ({ isGeneratingQuestions }: { isGeneratingQuestions: boolean }) => (
  <>
    <CircleNotch className="size-4 animate-spin" />
    <span>{isGeneratingQuestions ? t`Analyzing...` : t`Optimizing...`}</span>
  </>
);

// Brain animation component
const AnimatedBrain = ({ className, isLoading }: { className?: string; isLoading?: boolean }) => (
  <Brain
    className={`${className} ${isLoading ? "animate-pulse" : "animate-pulse"} transition-all duration-300`}
    weight="duotone"
    aria-hidden="true"
  />
);

// Floating tooltip component
const FloatingTooltip = ({ text, show }: { text: string; show: boolean }) => (
  <div
    className={`pointer-events-none absolute -bottom-12 left-1/2 -translate-x-1/2 transform transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`}
  >
    <div className="rounded-lg bg-gray-900 px-3 py-2 text-xs whitespace-nowrap text-white shadow-lg dark:bg-gray-800">
      {text}
      <div className="absolute top-0 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 transform bg-gray-900 dark:bg-gray-800" />
    </div>
  </div>
);

export const OverallScoreWithOptimize = ({
  analysesData,
  onOptimize,
  isOptimizing,
  isGeneratingQuestions,
  isLoadingOptimization,
  optimizationError,
}: OverallScoreWithOptimizeProps) => {
  const { isDarkMode } = useTheme();

  // Memoize expensive calculations
  const score = useMemo(
    () => Math.round(analysesData.overallWeightedScore * 100),
    [analysesData.overallWeightedScore],
  );

  const scoreLevel = useMemo(() => getScoreLevelConfig(score, isDarkMode), [score, isDarkMode]);

  // Memoize loading state
  const isLoading = useMemo(
    () => isOptimizing || isLoadingOptimization || isGeneratingQuestions,
    [isOptimizing, isLoadingOptimization, isGeneratingQuestions],
  );

  // Optimize event handlers
  const handleOptimizeClick = useCallback(() => {
    if (!isLoading) {
      onOptimize();
    }
  }, [onOptimize, isLoading]);

  const IconComponent = scoreLevel.icon;

  return (
    <div className="w-full space-y-2 px-10">
      <div
        className="bg-card rounded-lg border-0 px-4 py-2 transition-all duration-300"
        role="region"
        aria-label={t`Resume score analysis`}
      >
        <div className="flex items-center gap-6">
          {/* Enhanced Gauge Section */}
          <div className="relative w-35 flex-shrink-0">
            <Gauge score={score} size="small" type="semicircle" />
          </div>

          {/* Enhanced Information Section */}
          <div className="ml-25 min-w-0 flex-1 space-y-2">
            {/* Score Level Badge */}
            <div className="mb-1 flex items-center gap-3">
              <span
                className="inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold whitespace-nowrap"
                style={{
                  color: scoreLevel.color,
                  backgroundColor: scoreLevel.bgColor,
                }}
              >
                <IconComponent className="mr-1 size-3" aria-hidden="true" />
                {scoreLevel.level}
              </span>

              {/* Success indicator for high scores */}
              {score >= SCORE_THRESHOLDS.EXCELLENT && (
                <>
                  <CheckCircle
                    className="size-4 text-green-500"
                    aria-label={t`High score achieved`}
                  />
                  <span className="text-green-500">{t`High score achieved!`}</span>
                </>
              )}
            </div>

            {/* Description */}
            <p className="text-muted-foreground text-sm leading-relaxed">
              {scoreLevel.description}
            </p>

            {/* Enhanced Action Button Section */}
            <div className="flex-shrink-0">
              <div
                className="group relative"
              >
                <button
                  className={cn(
                    "group relative w-full max-w-48 transform cursor-pointer overflow-hidden rounded-xl border px-1 py-2 font-semibold shadow-xl transition-transform duration-300 hover:scale-[1.04] focus:ring-4 focus:outline-none dark:text-white",
                    "border-gray-300 bg-gradient-to-r from-slate-200 via-slate-200 to-slate-200 text-slate-700",
                    "dark:border-slate-600! dark:bg-gradient-to-r dark:from-slate-800 dark:via-slate-600 dark:to-slate-700 dark:focus:ring-slate-400",
                  )}
                  onClick={handleOptimizeClick}
                  disabled={isLoading}
                  aria-label={
                    isLoading
                      ? isGeneratingQuestions
                        ? t`Currently analyzing resume with AI`
                        : t`Currently optimizing resume with AI`
                      : t`Optimize resume with AI brain power`
                  }
                >
                  <div className="animate-shimmer absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent dark:via-white/10"></div>
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isLoading ? (
                      <LoadingSpinner isGeneratingQuestions={isGeneratingQuestions} />
                    ) : (
                      <>
                        <AnimatedBrain className="size-6 flex-shrink-0" isLoading={isLoading} />
                        <span className="text-md relative z-10 truncate">
                          {scoreLevel.buttonText}
                        </span>
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced error display */}
      <ErrorMessage error={optimizationError} />
    </div>
  );
};
